import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input"
import { useLocation, useSearchParams } from "wouter";
import useAuth from "@/contexts/auth/auth-context";
import useMusioApi from "@/contexts/client/musio-client-context";
import NewAuthMiddleware from "@/api/middleware/auth-middleware";
import { LoadingIndicator } from "@/components/ui/loading-indicator";
import { ReactComponent as Logo } from "@/assets/musio-logos/logo_center_top.svg";
import { TOKEN_KEY } from "@/contexts/auth/auth-context";
import { NotificationRow } from "@/components/ui/notification-row";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import useFingerprint from "@/hooks/use-fingerprint";
import * as Sentry from "@sentry/react";

const KEY_REDIRECT = "redirect-to";

export default function LoginPage() {
  const [_, navigate] = useLocation();
  const { musioApi, useMiddleware } = useMusioApi();
  const { login } = useAuth();
  const { visitorId, loading: isFingerprinting } = useFingerprint();

  const [checkingLocalStorage, setCheckingLocalStorage] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get(KEY_REDIRECT) || "account";

  // runs before everything else to set the token and get user info if token is valid
  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) {
      setToken(t);
      return;
    }
    setCheckingLocalStorage(false);
  }, []);

  useEffect(() => {
    if (!token) return;
    const mw = NewAuthMiddleware(token);
    useMiddleware(mw);
    refetch();
  }, [token]);

  // just run this once invalidate immediatley after failing or login after it passes
  const {
    data: user,
    isSuccess,
    isError,
    error,
    refetch,
  } = musioApi.useQuery("get", "/user", {}, {
    enabled: false,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  //core reactive use effect for when user attempts to login
  useEffect(() => {
    if (!token) return;

    if (isError) {
      console.error(error);
      // reset all of the setup for next attempt
      localStorage.removeItem(TOKEN_KEY);
      setErrorMessage("Failed to fetch user data");
      setToken(null);
      setCheckingLocalStorage(false);
      Sentry.captureException(error, {
        data: JSON.stringify(error),
      });

      return;
    }

    if (user) {
      login(user);
      navigate(`~/${redirectTo}`);
    }
  }, [token, user, isError, isSuccess, error, login, navigate, redirectTo]);

  const { mutate, isPending: isTokenPending } = musioApi.useMutation("post", "/token", {
    onSuccess: (data) => {
      localStorage.setItem(TOKEN_KEY, data.token!);
      setToken(data.token!); // triggers the query + middleware effect
    },
    onError: (err) => {
      console.error(err);
      setErrorMessage("Failed to login");
    },
    gcTime: 0,
  });

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    mutate({
      body: {
        apiKey: import.meta.env.VITE_API_KEY,
        authId: email,
        password,
        machineId: visitorId!,
      },
    });
  };

  if (checkingLocalStorage || isFingerprinting) return null;

  return (
    <ScrollArea className="h-full">
      <div className="flex flex-col items-center justify-center font-inter space-y-2">
        <div className="w-[128px] h-[128px] flex-shrink-0 flex justify-center items-center">
          <Logo />
        </div>
        <form onSubmit={handleLogin} className="max-w-[478px] w-full">
          <Card className="flex items-center justify-center flex-col musio-glass w-full ">
            <CardHeader className="flex items-center justify-center">
              <div className="flex items-center justify-between text-musio-lilac">
                <CardTitle className="text-[30px]">Login</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="w-full space-y-2 card-content-padded">
              {errorMessage ?
                <NotificationRow intent={"error"}>
                  {errorMessage}
                </NotificationRow> : null
              }
              <Input
                type="email"
                id="email"
                placeholder="Email."
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="bg-musio-bg-light border-musio-bg-dark"
              />
              <Input
                typeof="text"
                type="password"
                id="password"
                placeholder="Password."
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="bg-musio-bg-light border-musio-bg-dark"
              />
              <Button
                type="button"
                onClick={() => navigate("~/recover")}
                variant="link"
                className="float-end h-auto p-3 text-xs text-musio-primary"
              >
                Forgot Password?
              </Button>
            </CardContent>
            <CardFooter className="p-5 w-full flex flex-col justify-center items-center gap-2">
              {isTokenPending ? (
                <LoadingIndicator />
              ) : (
                <>
                  <Button
                    type="submit"
                    className="w-full font-bold bg-musio-green text-musio-mid-gray"
                  >
                    Login
                  </Button>
                  <Button
                    type="button"
                    className="w-full border-primary bg-musio-mid-gray font-bold text-musio-lilac border-none"
                    variant="outline"
                    onClick={e => {
                      e.preventDefault();
                      navigate("/signup");
                    }}
                  >
                    Create Account
                  </Button>
                </>
              )}
            </CardFooter>
          </Card>
        </form>
      </div>
      <ScrollBar orientation="vertical" />
    </ScrollArea>
  );
}
